import React, { useState, useRef, useEffect } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useMsal } from '@azure/msal-react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import InfoModal from './common/InfoModal';
import api from '../services/api';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';

interface CustomParkingLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: string;
  driverName: string;
  onSave: (newLocationRecno: number) => void;
}

interface PlaceDetails {
  streetAddress: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
}

const CustomParkingLocationModal: React.FC<CustomParkingLocationModalProps> = ({
  isOpen,
  onClose,
  driver,
  driverName,
  onSave,
}) => {
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  const defaultLocationName = `${driver} - ${driverName} - Parking`;

  const [locationName, setLocationName] = useState(defaultLocationName);
  const [addressInput, setAddressInput] = useState('');
  const [placeDetails, setPlaceDetails] = useState<PlaceDetails | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocationName(`${driver} - ${driverName} - Parking`);
      setAddressInput('');
      setPlaceDetails(null);
      setError(null);
    }
  }, [isOpen, driver, driverName]);

  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      if (place.geometry && place.address_components) {
        let streetNumber = '';
        let route = '';
        let city = '';
        let county = '';
        let state = '';
        let zip = '';

        for (const component of place.address_components) {
          const types = component.types;

          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_2')) {
            // County - remove " County" suffix if present
            county = component.long_name.replace(/ County$/i, '');
          } else if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          } else if (types.includes('postal_code')) {
            zip = component.long_name;
          }
        }

        const streetAddress = streetNumber ? `${streetNumber} ${route}` : route;

        setAddressInput(place.formatted_address || streetAddress);
        setPlaceDetails({
          streetAddress,
          city,
          county,
          state,
          zip,
          lat: place.geometry.location?.lat() || 0,
          lng: place.geometry.location?.lng() || 0,
        });
        setError(null);
      }
    }
  };

  const handleSave = async () => {
    if (!placeDetails) {
      setError('Please select an address from the suggestions');
      return;
    }

    if (!locationName.trim()) {
      setError('Please enter a location name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await api.post('/admin/create-parking-location', {
        lname: locationName.trim(),
        ladd: placeDetails.streetAddress,
        lcity: placeDetails.city,
        lcounty: placeDetails.county,
        lstate: placeDetails.state,
        lzip: placeDetails.zip,
        coordinates: `${placeDetails.lat},${placeDetails.lng}`,
        createdby: userEmail,
      });

      onSave(response.data.recno);
      onClose();
    } catch (err) {
      console.error('Error creating parking location:', err);
      setError('Failed to create parking location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Alt+S saves, Alt+C closes — both mirroring the footer buttons' saving guard.
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: isOpen },
  );

  return (
    <InfoModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Custom Parking Location"
      maxWidth="lg"
      body={
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-label mb-1">
              Location Name
            </label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Enter location name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-1">
              Search Address
            </label>
            {isLoaded ? (
              <Autocomplete
                onLoad={onAutocompleteLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: 'us' },
                  types: ['address'],
                }}
              >
                <input
                  type="text"
                  value={addressInput}
                  onChange={(e) => setAddressInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Start typing an address..."
                />
              </Autocomplete>
            ) : (
              <input
                type="text"
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 dark:bg-gray-600 dark:border-gray-600 dark:text-gray-400"
                placeholder="Loading Google Maps..."
              />
            )}
          </div>

          {placeDetails && (
            <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg space-y-1">
              <div className="text-sm font-medium text-green-800 dark:text-green-200">
                Selected Address
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">
                <div>{placeDetails.streetAddress}</div>
                <div>
                  {placeDetails.city}, {placeDetails.state} {placeDetails.zip}
                </div>
                {placeDetails.county && (
                  <div className="text-xs text-green-600 dark:text-green-400">
                    {placeDetails.county} County
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      }
      buttons={[
        {
          label: 'Cancel',
          onClick: onClose,
          variant: 'secondary' as const,
        },
        {
          label: saving ? 'Creating...' : 'Create Location',
          onClick: handleSave,
          variant: 'primary' as const,
          disabled: saving || !placeDetails,
        },
      ]}
    />
  );
};

export default CustomParkingLocationModal;
